import {type FocusEvent, type ReactNode, useState} from 'react'
import { useTranslation } from 'react-i18next'
import {Tooltip} from '@/components/ui/Tooltip'
import {DropdownMenu, type DropdownMenuItem} from '@/components/ui/DropdownMenu'
import {AvatarStack, avatarColor} from '@/components/ui/AvatarStack'
import {CalendarEditIcon, EditIcon, FolderOpenIcon, MoveIcon, ProjectIcon, TrashIcon, UsersIcon} from '@/components/ui/icons'
import type {ProjectListItem} from '@/api/types'
import { formatDate } from '@/utils/date'

function extractDialect(project: ProjectListItem): string | null {
  if (project.dialect) return project.dialect
  const commit = project.current_commit as { metadata?: unknown } | undefined
  const meta = commit?.metadata as { project?: { dialect?: string } } | undefined
  return meta?.project?.dialect ?? null
}

interface ProjectListCardProps {
  project: ProjectListItem
  onOpen: (project: ProjectListItem) => void
  canDelete?: boolean
  canEdit?: boolean
  canMove?: boolean
  canManageAccess?: boolean
  onDelete?: (projectId: number) => void
  onEdit?: (project: ProjectListItem) => void
  onMove?: (project: ProjectListItem) => void
  onManageAccess?: (project: ProjectListItem) => void
  badge?: ReactNode
  hideIcon?: boolean
  isOpen?: boolean
}

export function ProjectListCard({
  project,
  onOpen,
  canDelete = false,
  canEdit = false,
  canMove = false,
  canManageAccess = false,
  onDelete,
  onEdit,
  onMove,
  onManageAccess,
  badge,
  hideIcon = false,
  isOpen = false,
}: ProjectListCardProps) {
  const { t } = useTranslation()
  const [isActive, setIsActive] = useState(false)

  function handleFocus() {
    setIsActive(true)
  }

  function handleBlur(event: FocusEvent<HTMLElement>) {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return
    setIsActive(false)
  }

  const dialect = extractDialect(project)
  const menuItems: DropdownMenuItem[] = [
    onEdit
      ? {
          id: 'edit',
          icon: <EditIcon />,
          title: t('common.edit'),
          description: t('projects.editDesc'),
          disabled: !canEdit,
          onSelect: () => onEdit(project),
        }
      : null,
    canMove && onMove
      ? {
          id: 'move',
          icon: <MoveIcon />,
          title: t('toolbar.move'),
          description: t('projects.moveDesc'),
          onSelect: () => onMove(project),
        }
      : null,
    onManageAccess
      ? {
          id: 'manage-access',
          icon: <UsersIcon />,
          title: t('manageAccess.title'),
          description: t('projects.manageAccessDesc'),
          disabled: !canManageAccess,
          onSelect: () => onManageAccess(project),
        }
      : null,
    canDelete && onDelete
      ? {
          id: 'delete',
          icon: <TrashIcon />,
          title: t('common.delete'),
          description: t('projects.deleteDesc'),
          onSelect: () => onDelete(project.id),
        }
      : null,
  ].filter((item): item is DropdownMenuItem => item !== null)
  const hasMenu = menuItems.length > 0

  const memberPreviewItems = (project.member_preview ?? []).map((m) => ({
    id: m.id,
    name: m.name,
    email: m.email,
    color: avatarColor(m.id),
  }))

  return (
    <Tooltip content={t('projects.openProject')} delayMs={100}>
      <div
        role="button"
        tabIndex={0}
        aria-label={t('projects.openProjectAria', { name: project.name })}
        onClick={() => onOpen(project)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onOpen(project)
          }
        }}
        onMouseEnter={() => setIsActive(true)}
        onMouseLeave={() => setIsActive(false)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        style={{
          background: 'var(--bg-card)',
          border: `1px solid ${isActive ? 'var(--accent)' : 'var(--border)'}`,
          borderRadius: 8,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 14,
          padding: '12px 16px',
          transition: 'border-color 0.15s, box-shadow 0.15s',
          cursor: 'pointer',
          boxShadow: isActive
            ? '0 0 0 1px color-mix(in srgb, var(--accent) 20%, transparent)'
            : 'none',
          outline: 'none',
          overflow: 'hidden',
        }}
      >
        {/* Project icon */}
        {!hideIcon && (
          <div
            style={{
              flexShrink: 0,
              width: 40,
              height: 40,
              borderRadius: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: isOpen ? 'var(--color-success-bg)' : 'var(--accent-muted)',
              border: `1px solid ${isOpen ? 'var(--color-success-border)' : 'var(--accent-border)'}`,
              color: isOpen ? 'var(--color-success)' : 'var(--accent)',
            }}
          >
            {isOpen ? <FolderOpenIcon /> : <ProjectIcon />}
          </div>
        )}

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {/* Name + menu */}
          <div
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
          >
            <Tooltip content={project.name} delayMs={400}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 14,
                  minWidth: 0,
                  flex: 1,
                  color: 'var(--text)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {project.name}
              </div>
            </Tooltip>

            {hasMenu && (
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  flexShrink: 0,
                  opacity: isActive ? 1 : 0,
                  pointerEvents: isActive ? 'auto' : 'none',
                  transition: 'opacity 0.15s',
                }}
              >
                <DropdownMenu
                  label={`Ações de ${project.name}`}
                  iconOnly
                  size="sm"
                  borderless
                  icon={
                    <span
                      aria-hidden="true"
                      style={{
                        display: 'inline-flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        width: 12,
                      }}
                    >
                      <span
                        style={{
                          width: 3,
                          height: 3,
                          borderRadius: '50%',
                          background: 'currentColor',
                        }}
                      />
                      <span
                        style={{
                          width: 3,
                          height: 3,
                          borderRadius: '50%',
                          background: 'currentColor',
                        }}
                      />
                      <span
                        style={{
                          width: 3,
                          height: 3,
                          borderRadius: '50%',
                          background: 'currentColor',
                        }}
                      />
                    </span>
                  }
                  sections={[
                    {
                      id: 'actions',
                      items: menuItems,
                    },
                  ]}
                />
              </div>
            )}

            {!hasMenu && badge && <div style={{ flexShrink: 0 }}>{badge}</div>}
          </div>

          {/* Avatars */}
          {memberPreviewItems.length > 0 && (
            <AvatarStack
              items={memberPreviewItems}
              max={5}
              size={22}
              ringColor="var(--bg-card)"
              totalCount={project.member_count}
            />
          )}

          {/* Dialect + date */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            {dialect ? (
              <span
                style={{
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  padding: '2px 6px',
                  borderRadius: 4,
                  background: 'var(--accent-muted)',
                  color: 'var(--accent)',
                  border: '1px solid var(--accent-border)',
                  flexShrink: 0,
                }}
                title={t('projects.dialectTitle')}
              >
                {dialect}
              </span>
            ) : (
              <span />
            )}

            <div
              style={{
                fontSize: 11,
                opacity: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                flexShrink: 0,
              }}
            >
              <CalendarEditIcon />
              <span>{formatDate(project.modified_at)}</span>
            </div>
          </div>

          {hasMenu && badge && <div>{badge}</div>}
        </div>
      </div>
    </Tooltip>
  )
}
